import React from 'react';
import { Box, Grid, Link, List, ListItem, Typography } from '@mui/material';
import { Instagram, LinkedIn, Twitter, YouTube } from '@mui/icons-material';
import RootineButton from '../../atoms/RootineButton';
import { colors } from '../../utils/theme';
import { RootineDivider } from '../../atoms/RootineDivider';
import { CustomIcon } from '../../atoms';
import { SHOPIFY_WEB_URL } from '../../config';

const SocialMedia: React.FC<{
  ariaLabel: string;
  link: string;
  icon: React.ReactElement;
}> = ({ ariaLabel, link, icon }) => {
  return (
    <Box
      sx={{
        background: colors.greys.BRAND_BLACK,
        borderRadius: 1,
        px: 0.5,
        pt: 0.5,
      }}
    >
      <a
        aria-label={ariaLabel}
        href={link}
        target="_blank"
        rel="noreferrer noopener"
      >
        {icon}
      </a>
    </Box>
  );
};

export const RootineFooter = (): JSX.Element => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      linkGroupTitle: 'Shop',
      linkGroupItems: [
        {
          ariaLabel: 'Shop all',
          label: 'Shop All',
          link: `${SHOPIFY_WEB_URL}/collections/shop-all`,
        },
        {
          ariaLabel: 'Sample 3 Pack',
          label: 'Sample 3 Pack',
          link: `${SHOPIFY_WEB_URL}/products/sample-3-pack`,
        },
        {
          ariaLabel: 'Bundle & Save',
          label: 'Bundle & Save',
          link: `${SHOPIFY_WEB_URL}/collections/bundles`,
        },
        {
          ariaLabel: 'Shop Gift Card',
          label: 'Gift Card',
          link: `${SHOPIFY_WEB_URL}/products/gift-cards`,
        },
        {
          ariaLabel: 'Wholesale on Faire',
          label: 'Buy Wholesale on Faire',
          link: `${SHOPIFY_WEB_URL}/pages/faire`,
        },
      ],
    },
    {
      linkGroupTitle: 'Learn',
      linkGroupItems: [
        {
          ariaLabel: 'Our Science',
          label: 'Our Science',
          link: `${SHOPIFY_WEB_URL}/pages/science`,
        },
        {
          ariaLabel: 'The Journal',
          label: 'The Journal',
          link: `${SHOPIFY_WEB_URL}/blogs/ourscience`,
        },
        {
          ariaLabel: 'Nutrients',
          label: 'Nutrients',
          link: `${SHOPIFY_WEB_URL}/pages/nutrients`,
        },
      ],
    },
    {
      linkGroupTitle: 'Support',
      linkGroupItems: [
        {
          ariaLabel: 'Accessibility',
          label: 'Accessibility',
          link: `${SHOPIFY_WEB_URL}/pages/accessibilty-statement`,
        },
        {
          ariaLabel: 'Help Center',
          label: 'Help Center',
          link: 'https://help.rootine.co/',
        },
      ],
    },
    {
      linkGroupTitle: 'About',
      linkGroupItems: [
        {
          ariaLabel: 'About Us',
          label: 'About Us',
          link: `${SHOPIFY_WEB_URL}/pages/about`,
        },
        {
          ariaLabel: 'Careers',
          label: 'Careers',
          link: 'https://jobs.ashbyhq.com/rootine',
        },
        {
          ariaLabel: 'Affiliates & Creator Partners',
          label: 'Affiliates & Creator Partners',
          link: `${SHOPIFY_WEB_URL}/pages/collab`,
        },
      ],
    },
  ];
  const legalLinks = [
    {
      ariaLabel: 'Terms of Service',
      label: 'Terms of Service',
      link: `${SHOPIFY_WEB_URL}/policies/terms-of-service`,
    },
    {
      ariaLabel: 'Privacy Policy',
      label: 'Privacy Policy',
      link: `${SHOPIFY_WEB_URL}/policies/privacy-policy`,
    },
  ];

  return (
    <>
      <RootineDivider dividerProps={{ sx: { my: 4 }, flexItem: true }} />

      <Grid container flexDirection="column" sx={{ mb: 4 }}>
        <Grid
          container
          item
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Grid item xs={12} md={3} sx={{ mb: { xs: 6 } }}>
            <Typography
              fontSize={{ xs: 24, md: 36 }}
              sx={{ lineHeight: 1.2, mb: 2 }}
            >
              Cheers to a better supplement routine.
            </Typography>
            <RootineButton
              label="Shop all"
              id="shop-all"
              aria-label="Shop all"
              variation="primary"
              size="medium"
              hideIcon
              fullWidth={false}
              as="a"
              url={`${SHOPIFY_WEB_URL}/collections/shop-all`}
              openNewTab
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: { xs: 'flex-start', md: 'space-evenly' },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              gap: { xs: 3, md: 0 },
            }}
          >
            {footerSections.map((section) => (
              <Box
                display="flex"
                flexDirection="column"
                sx={{ width: '45%' }}
                key={`footer-${section.linkGroupTitle}`}
              >
                <Typography variant="bold" fontSize={20}>
                  {section?.linkGroupTitle}
                </Typography>
                <List dense sx={{ mt: 2 }}>
                  {section.linkGroupItems?.map((sectionItems) => (
                    <ListItem
                      disableGutters
                      key={`footer-${sectionItems.label}`}
                    >
                      <Link
                        href={
                          sectionItems?.link.startsWith('https://')
                            ? sectionItems.link
                            : `${SHOPIFY_WEB_URL}${sectionItems?.link}`
                        }
                        target="_blank"
                        rel="noreferrer noopener"
                        underline="hover"
                      >
                        <Typography fontSize={16} sx={{ opacity: 0.7 }}>
                          {sectionItems?.label}
                        </Typography>
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Grid>
        </Grid>

        <Grid container flexDirection="column" sx={{ mt: 10, mb: 3 }}>
          <Grid
            container
            item
            xs={12}
            flexDirection="row"
            justifyContent={{ xs: 'flex-start', md: 'center' }}
            gap={3}
          >
            <SocialMedia
              ariaLabel="Visit Rootine's Instagram profile"
              link="https://www.instagram.com/rootine_co/"
              icon={
                <Instagram
                  fontSize="small"
                  sx={{ color: colors.greys.BASE_WHITE }}
                />
              }
            />
            <SocialMedia
              ariaLabel="Visit Rootine's Twitter profile"
              link="https://twitter.com/rootine_co"
              icon={
                <Twitter
                  fontSize="small"
                  sx={{ color: colors.greys.BASE_WHITE }}
                />
              }
            />
            <SocialMedia
              ariaLabel="Visit Rootine's LinkedIn profile"
              link="https://www.linkedin.com/company/rootine"
              icon={
                <LinkedIn
                  fontSize="small"
                  sx={{ color: colors.greys.BASE_WHITE }}
                />
              }
            />
            <SocialMedia
              ariaLabel="Visit Rootine's Youtube profile"
              link="https://www.youtube.com/@Rootine"
              icon={
                <YouTube
                  fontSize="small"
                  sx={{ color: colors.greys.BASE_WHITE }}
                />
              }
            />
            <SocialMedia
              ariaLabel="Visit Rootine's TiktTok profile"
              link="https://www.tiktok.com/@rootine_co"
              icon={
                <CustomIcon
                  name="tiktok"
                  sx={{
                    color: colors.greys.BASE_WHITE,
                    fontSize: 20,
                    px: 0.1,
                    mt: 0.2,
                  }}
                />
              }
            />
          </Grid>
        </Grid>

        <Grid container flexDirection="column">
          <Grid
            container
            item
            flexDirection="row"
            justifyContent={{ xs: 'flex-start', md: 'center' }}
          >
            <Typography fontSize={12} sx={{ opacity: 0.7 }}>
              &copy; {currentYear} Rootine
            </Typography>
            <RootineDivider
              dividerProps={{
                orientation: 'vertical',
                flexItem: true,
                sx: { mx: 1 },
              }}
            />
            <Typography fontSize={12} sx={{ opacity: 0.7 }}>
              All Rights Reserved
            </Typography>

            {legalLinks && (
              <>
                {legalLinks.map((link) => (
                  <Box display="flex" key={`footer-${link.label}`}>
                    <RootineDivider
                      dividerProps={{
                        orientation: 'vertical',
                        flexItem: true,
                        sx: { mx: 1 },
                      }}
                    />

                    <Link
                      aria-label={link?.ariaLabel}
                      href={link.link}
                      target="_blank"
                      rel="noreferrer noopener"
                      underline="hover"
                    >
                      <Typography fontSize={12} sx={{ opacity: 0.7 }}>
                        {link?.label}
                      </Typography>
                    </Link>
                  </Box>
                ))}
              </>
            )}

            <Typography
              textAlign={{ md: 'center' }}
              fontSize={12}
              sx={{ opacity: 0.7, mt: 1.5 }}
            >
              ROOTINE *These statements have not been evaluated by the Food and
              Drug Administration. This product is not intended to diagnose,
              treat, cure or prevent any disease. Always consult with your
              healthcare provider before starting any supplement program or
              starting any new lifestyle plan. Information on this site is
              provided for informational purposes only.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
